import { Injectable } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, catchError, filter, from, of, tap } from 'rxjs';
import { ExpensesDataSource } from '../data/expenses.data-source';
import {
  CreateExpensePayload,
  Expense,
  ExpenseDriverOption,
  ExpenseRef,
  UpdateExpensePayload,
} from '../models/expense.model';
import { ExpenseFormSidebarComponent } from '../components/expense-form-sidebar/expense-form-sidebar.component';
import {
  ExpenseFormMode,
  ExpenseFormResult,
  ExpenseReceiptIntent,
} from '../components/expense-form-sidebar/expense-form-sidebar.model';

/**
 * Abre el formulario de alta o edición y resuelve el guardado.
 *
 * Vive aquí, y no en el contenedor de la pantalla, porque el formulario se abre
 * desde dos sitios: el listado de Gastos y el desglose de precio del detalle de
 * servicio. Antes sólo lo tenía el contenedor, así que «Editar gasto» no hacía
 * nada al abrirse desde el desglose.
 *
 * Sólo emite cuando se ha guardado; cerrar el panel sin guardar completa sin
 * emitir. Cada pantalla decide después qué hacer: avisar, recargar o parchear la
 * fila.
 */
@Injectable()
export class ExpenseFormLauncherService {
  /**
   * Caché de conductores para la sesión: reabrir el panel no vuelve a pedir la
   * lista. Se comparte entre las dos pantallas, que usan la misma instancia.
   */
  private drivers: readonly ExpenseDriverOption[] = [];

  constructor(
    private readonly offcanvas: NgbOffcanvas,
    private readonly dataSource: ExpensesDataSource
  ) {}

  /**
   * Abre el panel y emite el gasto guardado.
   *
   * El guardado se ejecuta **dentro** del panel: si la API falla, el formulario
   * sigue abierto con lo escrito en lugar de perderse.
   */
  open(mode: ExpenseFormMode, expense: Expense | null): Observable<Expense> {
    const offcanvasRef = this.offcanvas.open(ExpenseFormSidebarComponent, {
      position: 'end',
      backdrop: 'static',
      panelClass: 'expenses-offcanvas',
    });

    const instance = offcanvasRef.componentInstance;
    instance.mode = mode;
    instance.expense = expense;
    instance.driversLoader = this.driversLoader();
    instance.submitHandler = (result: ExpenseFormResult) =>
      this.persist(expense, result);

    return from(offcanvasRef.result as Promise<Expense>).pipe(
      filter((saved): saved is Expense => !!saved),
      /*
       * Cerrar sin guardar rechaza la promesa de `result`. Aquí no es un error:
       * es que no hay nada que propagar, así que se completa en silencio.
       */
      catchError(() => EMPTY)
    );
  }

  private driversLoader(): Observable<readonly ExpenseDriverOption[]> {
    if (this.drivers.length > 0) {
      return of(this.drivers);
    }

    return this.dataSource
      .searchDrivers()
      .pipe(tap((drivers) => (this.drivers = drivers)));
  }

  /** Operación de guardado que ejecuta el panel: crear o actualizar. */
  private persist(
    expense: Expense | null,
    result: ExpenseFormResult
  ): Observable<Expense> {
    if (result.mode === 'edit' && expense) {
      const ref = this.refFor(expense);

      if (!ref) {
        return EMPTY;
      }

      const payload: UpdateExpensePayload = {
        category: result.values.category,
        description: result.values.description,
        amountInCents: result.values.amountInCents,
        status: result.values.status,
        reason: result.values.reason,
        reasonCode: result.values.reasonCode,
        receiptFile: fileFrom(result.receipt),
        removeReceipt: result.receipt.kind === 'removed',
      };

      return this.dataSource.update(ref, payload);
    }

    const payload: CreateExpensePayload = {
      nodeId: result.values.nodeId ?? '',
      driverId: result.values.driverId ?? '',
      driverName:
        this.drivers.find(
          (driver) => driver.driverId === result.values.driverId
        )?.fullName ?? null,
      licensePlate: result.values.licensePlate ?? '',
      category: result.values.category,
      description: result.values.description,
      amountInCents: result.values.amountInCents,
      status: result.values.status,
      reason: result.values.reason,
      reasonCode: result.values.reasonCode,
      receiptFile: fileFrom(result.receipt),
    };

    return this.dataSource.create(payload);
  }

  /** Los gastos históricos no tienen identificador: no se pueden editar. */
  private refFor(expense: Expense): ExpenseRef | null {
    return expense.expenseId
      ? { nodeId: expense.nodeId, expenseId: expense.expenseId }
      : null;
  }
}

/**
 * Sólo se sube fichero al sustituir el comprobante. Quitarlo viaja aparte, en el
 * flag `removeReceipt` de la edición.
 */
function fileFrom(intent: ExpenseReceiptIntent): File | null {
  return intent.kind === 'new' ? intent.file : null;
}
